// ADO-2262: keeps an element mounted through its exit (the prototype's `useMotionPresence`).
//
// `present` turns true as soon as `open` does. When `open` turns false the element stays
// `present` and `closing` (the host marks it so the CSS runs the exit, and lets it take no
// input) until the host calls `finish` from the element's own `transitionend` or
// `animationend`, or until the exit's length and a grace have passed, should the browser never
// report the end (a background tab, a cancelled transition). Under reduced motion it goes at
// once. Reopening mid-exit keeps the element, and a late `finish` then does nothing.
import { useCallback, useEffect, useRef, useState } from 'react';
import { EXIT_GRACE_MS, prefersReducedMotion } from '../motion';

export interface ExitTransition {
  /** Whether to render the element: open, or closing. */
  readonly present: boolean;
  /** Present but no longer open: running its exit. */
  readonly closing: boolean;
  /** The exit has ended: unmount. Does nothing once the element is open again. */
  readonly finish: () => void;
}

export function useExitTransition(open: boolean, exitMs: number): ExitTransition {
  const [present, setPresent] = useState(open);
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) setPresent(true);
    else if (prefersReducedMotion()) setPresent(false);
  }
  const closing = present && !open;

  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  const finish = useCallback(() => {
    if (!openRef.current) setPresent(false);
  }, []);

  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(finish, exitMs + EXIT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [closing, exitMs, finish]);

  return { present, closing, finish };
}
